import { useCallback, useEffect, useMemo, useState } from 'react';

import { type ClientListing, ListingStatus } from 'src/generated/graphql';
import { postListing } from 'src/services/api';
import { ConsoleErrorId, consoleErrorWrapper } from 'src/utils';

type ClientListingSubset = Pick<ClientListing, 'id'>;

export type ClientListingPostUpdate = Pick<
  ClientListing,
  | 'autoRenew'
  | 'creditRemaining'
  | 'expiryDate'
  | 'legacyProductCode'
  | 'status'
>;

export type UsePostListingStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'success'; listing: ClientListingPostUpdate }
  | { kind: 'error'; message: string };

export const usePostListing = (listing: ClientListingSubset) => {
  const [status, setStatus] = useState<UsePostListingStatus>({
    kind: 'idle',
  });

  const post = useCallback(async () => {
    setStatus({ kind: 'loading' });

    try {
      const responseListing = await postListing(listing.id.toString());
      validateResponse(responseListing);

      const clientListing: ClientListingPostUpdate = {
        autoRenew: responseListing.autoRenew,
        creditRemaining: responseListing.creditRemaining,
        expiryDate: responseListing.expiryDate,
        legacyProductCode: responseListing.productCode,
        status: ListingStatus.Active,
      };

      const resultStatus: UsePostListingStatus = {
        kind: 'success',
        listing: clientListing,
      };

      setStatus(resultStatus);
      return resultStatus;
    } catch (error: any) {
      const resultStatus: UsePostListingStatus = {
        kind: 'error',
        message: error?.errorMessage ?? error?.status ?? 'Something went wrong',
      };
      setStatus(resultStatus);
      return resultStatus;
    }
  }, [listing.id]);

  useEffect(() => {
    // Reset this hook's status state if the underlying listing is updated. We want to do
    // this so that if the underlying listing is refreshed (e.g. via a "refetchQueries"
    // call) then we allow the user to act on this listing again. If we don't do
    // this then the local state will remain stale which may make subsequent attempts
    // to post fail (no-op)
    if (status.kind === 'success' || status.kind === 'error') {
      setStatus({ kind: 'idle' });
    }
  }, [status.kind, listing]);

  return useMemo(() => ({ status, post }), [status, post]);
};

export const usePostListingById = () => {
  const [status, setStatus] = useState<UsePostListingStatus>({
    kind: 'idle',
  });

  const post = useCallback(async (listingId: string) => {
    setStatus({ kind: 'loading' });

    try {
      const responseListing = await postListing(listingId);
      validateResponse(responseListing);

      const clientListing: ClientListingPostUpdate = {
        autoRenew: responseListing.autoRenew,
        creditRemaining: responseListing.creditRemaining,
        expiryDate: responseListing.expiryDate,
        legacyProductCode: responseListing.productCode,
        status: ListingStatus.Active,
      };

      const resultStatus: UsePostListingStatus = {
        kind: 'success',
        listing: clientListing,
      };

      setStatus(resultStatus);
      return resultStatus;
    } catch (error: any) {
      const resultStatus: UsePostListingStatus = {
        kind: 'error',
        message: error?.errorMessage ?? error?.status ?? 'Something went wrong',
      };
      setStatus(resultStatus);
      return resultStatus;
    }
  }, []);

  return useMemo(() => ({ status, post }), [status, post]);
};

// Log so we know but don't do anything else... we don't want to interrupt current handling
const validateResponse = (response: any) => {
  const isValid =
    typeof response.autoRenew === 'boolean' &&
    typeof response.creditRemaining === 'number' &&
    typeof response.expiryDate === 'string' &&
    typeof response.productCode === 'string';

  if (!isValid) {
    consoleErrorWrapper(
      ConsoleErrorId.PostListingValidation,
      `Post listing response payload is not correct. Payload: ${JSON.stringify(
        response,
      )}`,
    );
  }
};
